// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FulfillmentOrderStatusChip } from '@/components/fulfillment-order-status-chip';
import { Link } from '@/components/link';
import { ShippingCarrierTrackingLabel } from '@/components/shipping-carrier-tracking-label';
import { ArrowForward, ExpandMore } from '@/constants/hardfin-standard-icons';
import { FulfillmentOrderItem } from '@/fulfillment/api/types';
import { shipmentHasExtraItems } from '@/fulfillment/components/extra-items';
import { ExtraItemsAlert } from '@/fulfillment/components/extra-items-alert';
import { Shipment } from '@/shipment/api/types';
import { ShipmentStatusChip } from '@/shipment/components/shipment-status-chip';
import { outboundPlannedQuantity } from '@/shipment/utilities/shipment-planned-quantity';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

interface LineItemsTableProps {
  items: FulfillmentOrderItem[];
  shipments: Shipment[];
}

interface DeliveredColumnProps {
  pending: number;
  shipped: number;
  delivered: number;
  quarantined: number;
}

/**
 * Component that displays the unified delivered status column showing
 * pending, quarantined, shipped, and delivered quantities in an outlined
 * box. Quarantined sits before shipped because a unit is quarantined before
 * being shipped, never after.
 */
function DeliveredColumn(props: DeliveredColumnProps) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 1.5,
        minWidth: 200,
      }}
    >
      <Stack direction="row" spacing={2} justifyContent="center">
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Pending
          </Typography>
          <Typography variant="body1">{formatNumber(props.pending)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="caption"
            color={props.quarantined > 0 ? 'warning.main' : 'text.secondary'}
            display="block"
          >
            Quarantined
          </Typography>
          <Typography
            variant="body1"
            color={props.quarantined > 0 ? 'warning.main' : 'text.primary'}
          >
            {formatNumber(props.quarantined)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Shipped
          </Typography>
          <Typography variant="body1">{formatNumber(props.shipped)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Delivered
          </Typography>
          <Typography variant="body1">
            {formatNumber(props.delivered)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

interface RelatedShipmentsTableProps {
  shipments: Shipment[];
  productId?: string;
  partId?: string;
  foItemIds: Set<string>;
}

function RelatedShipmentsTable(props: RelatedShipmentsTableProps) {
  const { shipments, productId, partId } = props;

  // Filter and flatten shipment items to show only those matching this line item's product/part
  const filteredShipmentItems = useMemo(() => {
    const items: {
      shipment: Shipment;
      quantity: number;
    }[] = [];

    // Match items by ID (productId and partId are now both just item IDs)
    const targetItemId = productId || partId;
    if (targetItemId) {
      shipments.forEach((shipment) => {
        shipment.items.forEach((item) => {
          if (item.id === targetItemId) {
            items.push({
              shipment,
              quantity:
                outboundPlannedQuantity(
                  item.quantity,
                  item.quarantinedQuantity
                ) ?? 0,
            });
          }
        });
      });
    }

    // Sort by ship date descending (most recent first), with shipment ID as secondary sort for stability
    items.sort((a, b) => {
      const aDate = a.shipment.shippedAt?.toMillis() ?? 0;
      const bDate = b.shipment.shippedAt?.toMillis() ?? 0;

      // Sort by ship date descending
      if (bDate !== aDate) {
        return bDate - aDate;
      }

      // Secondary sort by shipment ID for stable sort
      return a.shipment.id.localeCompare(b.shipment.id);
    });

    return items;
  }, [shipments, productId, partId]);

  if (filteredShipmentItems.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No related shipments
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Related shipments
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Shipment ID</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Ship date</TableCell>
              <TableCell>Delivery date</TableCell>
              <TableCell>Tracking</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredShipmentItems.map((item, index) => {
              const { shipment, quantity } = item;

              return (
                <TableRow key={`${shipment.id}-${index}`}>
                  <TableCell>
                    <ShipmentStatusChip status={shipment.status} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Link
                        to="/shipments/$shipmentKey"
                        params={{ shipmentKey: shipment.key }}
                        search={{ type: 'outbound' }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {shipment.shipmentNumber}
                        <ArrowForward fontSize="small" />
                      </Link>
                      {shipmentHasExtraItems(shipment, props.foItemIds) && (
                        <ExtraItemsAlert />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{formatNumber(quantity)}</TableCell>
                  <TableCell>
                    {shipment.shippedAt
                      ? shipment.shippedAt.toLocaleString({
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {shipment.deliveredAt
                      ? shipment.deliveredAt.toLocaleString({
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <ShippingCarrierTrackingLabel
                      carrier={shipment.trackingNumberDetails?.carrier ?? null}
                      trackingIdentifier={
                        shipment.trackingNumberDetails?.trackingNumber ?? null
                      }
                      linkNumberDirectly={true}
                      variant="body2"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/**
 * Component that displays the line items of a fulfillment order in a card-based layout.
 * Shows product information and quantity tracking with inline labels.
 */
export function FulfillmentLineItemsTable(props: LineItemsTableProps) {
  const { items, shipments } = props;
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Build a set of item IDs from FO line items for detecting extra shipment items
  const foItemIds = useMemo(
    () => new Set(items.map((item) => item.item.id)),
    [items]
  );

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  if (items.length === 0) {
    return (
      <Box padding={3}>
        <Typography variant="body2" color="text.secondary">
          No line items
        </Typography>
      </Box>
    );
  }

  return items.map((item, index) => {
    const requested = item.requestedQuantity;
    const pending = item.pendingQuantity;
    // Held units are already excluded from the shipped tier upstream, so the
    // two buckets render as-is.
    const quarantined = item.quarantinedQuantity;
    const shipped = item.shippedQuantity;
    const delivered = item.deliveredQuantity;
    // Held units are excluded from pending, shipped and delivered upstream, so
    // they have to come off Need too or the Quarantined bucket counts them twice.
    const need = Math.max(
      0,
      requested - (pending + shipped + delivered + quarantined)
    );

    // Determine item display name and SKU
    const productName = item.item.name;
    const productSku = item.item.sku;

    const isExpanded = expandedItems.has(item.id);

    return (
      <Box
        key={item.id}
        sx={{
          ...(index < items.length - 1 && {
            borderBottom: '1px solid',
            borderColor: 'divider',
          }),
          paddingY: 1,
          paddingX: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Left side - Status and Product */}
          <Stack direction="row" spacing={2} alignItems="center" flex={1}>
            <FulfillmentOrderStatusChip status={item.status} />
            <Box>
              <Typography variant="body2">
                {productSku}{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  ({productName})
                </Typography>
              </Typography>
            </Box>
          </Stack>

          {/* Right side - Quantities */}
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Requested column */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary">
                Requested
              </Typography>
              <Typography variant="body1">{formatNumber(requested)}</Typography>
            </Box>

            {/* Delivered column - outlined with combined metrics */}
            <DeliveredColumn
              pending={pending}
              shipped={shipped}
              delivered={delivered}
              quarantined={quarantined}
            />

            {/* Need column */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary">
                Need
              </Typography>
              <Typography variant="body1">{formatNumber(need)}</Typography>
            </Box>

            {/* Expand button */}
            <IconButton
              size="small"
              onClick={() => toggleExpanded(item.id)}
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Stack>
        </Stack>

        {/* Expanded section with related shipments */}
        <Collapse in={isExpanded}>
          <Box paddingY={1} paddingX={2}>
            <RelatedShipmentsTable
              shipments={shipments}
              productId={item.item.id}
              foItemIds={foItemIds}
            />
          </Box>
        </Collapse>
      </Box>
    );
  });
}
